// from https://github.com/unclechu/node-deep-extend/blob/master/lib/deep-extend.js

function extend<A,T>(a?: A, b?: T | Array<T>, trace = <Array<TraceItem>>[], history = []) {
  let merger = new Merger(trace);
  return <A&T>merger.deepExtend(a, b, history);
}

interface TraceItem {
  path?: string;
  target: Object;
  key: string;
  value: any;
  was: any;
}

class Buffer {
  value: any;

  constructor(public length: number) {
  }

  copy(item) {
      this.value = item;
  }
}

class Merger {

  constructor(public trace: Array<TraceItem>) {
  }

  /**
   * @param a Object to be extended is the first argument
   * @param b Object with values to be copied into target is the second parameter
   *
   * @returns extended object or false if have no target object or incorrect type.
   *
   * If you wish to clone source object (without modify it), just use empty new
   * object as first argument, like this:
   *   deepExtend({}, yourObj_1, [yourObj_N]);
   */
  deepExtend<T>(a?: any, b?: T | Array<T>, trace = this.trace, history = []) {

      if (arguments.length < 1 || typeof a !== 'object') {
          return false;
      }

      if (!b) {
          return a;
      }

      var target = a;
      let args = <Array<T>>(Array.isArray(b) ? b : [b]);

      var val, src;

      if (-1 < history.indexOf(a)) {
          throw "circular reference detected";
      }
      history.push(a);

      args.forEach(obj => {

          // ignore functions
          if (typeof obj === 'function') {
              return;
          }

          // skip argument if it is array or isn't object
          if (typeof obj !== 'object') {
              return;
          }
          if (Array.isArray(obj)) {
              this.merge("id", history, target, this.deepCloneArray(obj, history));
              return;
          }

          Object.keys(obj).forEach(key => {
              src = target[key]; // source value
              val = obj[key]; // new value

              // recursion prevention
              if (val === target) {
                  return;

                  /**
                   * if new value isn't object then just overwrite by new value
                   * instead of extending.
                   */
              } else if (typeof val !== 'object' || val === null) {

                  // record change
                  trace.push({
                      key: key,
                      target: target,
                      was: target[key],
                      value: val
                  });

                  target[key] = val;

                  return;

                  // calix: merge arrays using 'id' attribute
              } else if (Array.isArray(val)) {

                  let result = this.merge("id", history, src, this.deepCloneArray(val, history));

                  // record change
                  trace.push({
                      key: key,
                      target: target,
                      was: target[key],
                      value: result
                  });

                  target[key] = result;

                  return;

                  // custom cloning and overwrite for specific objects
              } else if (this.isSpecificValue(val)) {
                  let result = this.cloneSpecificValue(val);

                  // record change
                  trace.push({
                      key: key,
                      target: target,
                      was: target[key],
                      value: result
                  });

                  target[key] = result;

                  return;

                  // overwrite by new value if source isn't object or array
              } else if (typeof src !== 'object' || src === null || Array.isArray(src)) {
                  let result = extend({}, val, history);

                  // record change
                  trace.push({
                      key: key,
                      target: target,
                      was: target[key],
                      value: result
                  });

                  target[key] = result;
                  return;

                  // source value and new value both objects, extending...
              } else {
                  let result = this.deepExtend(src, val, history);

                  // record change
                  trace.push({
                      key: key,
                      target: target,
                      was: target[key],
                      value: result
                  });

                  target[key] = result;

                  return;
              }
          });
      });

      return target;
  }

  private isSpecificValue(val) {
      return (
          val instanceof Buffer
          || val instanceof Date
          || val instanceof RegExp
      ) ? true : false;
  }

  private cloneSpecificValue(val): any {
      if (val instanceof Buffer) {
          var x = new Buffer(val.length);
          val.copy(x);
          return x;
      } else if (val instanceof Date) {
          return new Date(val.getTime());
      } else if (val instanceof RegExp) {
          return new RegExp(val.source);
      } else {
          throw new Error('Unexpected situation');
      }
  }

  /**
   * Recursive cloning array.
   */
  private deepCloneArray(arr, history = []) {
      var clone = [];
      arr.forEach((item, index) => {
          if (-1 < history.indexOf(item)) {
              throw "circular reference detected";
          }
          if (typeof item === 'object' && item !== null) {
              if (Array.isArray(item)) {
                  clone[index] = this.deepCloneArray(item, history);
              } else if (this.isSpecificValue(item)) {
                  clone[index] = this.cloneSpecificValue(item);
              } else {
                  // do not want to effect this.trace, just want to clone the item
                  clone[index] = extend({}, item, history);
              }
          } else {
              clone[index] = item;
          }
          history.push(clone[index]);
      });
      return clone;
  }

  // written by calix
  private merge(key: string, history = [], ...arrays: Array<any>[]) {

      // skip trivial arrays
      arrays = arrays.filter(a => !!a);
      var result = arrays.shift();
      if (!arrays.length) return result || [];

      var hash = {}; result.forEach((item, i) => hash[item.id] = i);

      arrays.forEach(array => {

          array.forEach(item => {
              let id = item[key];
              if (typeof hash[id] === "undefined") {
                  // a new item was created
                  hash[id] = result.push(item) - 1;

                  this.trace.push({
                      key: hash[id],
                      target: result,
                      value: item,
                      was: undefined
                  });

              } else {
                  // later items have higher priority but earlier items appear earlier in the array,
                  // could unshift instead of push but then hash would need refreshing
                  // any other solution?
                  let newVal = this.deepExtend(result[hash[id]], item, history);

                  // record change
                  this.trace.push({
                      key: hash[id],
                      target: result,
                      value: newVal,
                      was: result[hash[id]]
                  });

                  result[hash[id]] = newVal;
              }
          });

      });

      return result;

  }
}

export = extend;